import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authAPI, sipAPI, agentAPI, campaignsAPI, adminAPI, callsAPI, leadsAPI, advisorsAPI } from './services/api';
import { getSocket, disconnectSocket } from './services/socket';
import { initSIP, hangup, hangupManual, mute, hold, stopSIP, isRegistered, answer, requestMicPermission, playDTMFTone, initKeepAlive, attachRemoteAudio, joinRoom, leaveRoom, isRoomCall, getRoomInboundPackets } from './services/sipClient';
import TopBar from './components/TopBar';
import DialingTicker from './components/DialingTicker';
import LeftPanel from './components/LeftPanel';
import CustomerDetails from './components/CustomerDetails';
import CallFlow from './components/CallFlow';
import WebPhone from './components/WebPhone';
import DispositionModal from './components/DispositionModal';
import CallHistory from './components/CallHistory';
import CallbackList from './components/CallbackList';
import NoShowList from './components/NoShowList';
import ChatWidget from './components/ChatWidget';
import InterCallWidget from './components/InterCallWidget';
import logo from './logo.png';

// ── Login ──────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'campaign'
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [authedUser, setAuthedUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const submitCredentials = async e => {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const res = await authAPI.login(u, p);
      if (!['agent','supervisor'].includes(res.user.role)) throw new Error('Accès réservé aux agents et superviseurs');
      localStorage.setItem('agent_token', res.token);
      localStorage.setItem('agent_user', JSON.stringify(res.user));

      const list = await campaignsAPI.list().catch(() => []);
      const active = (list || []).filter(c => c.status === 'active' || c.status === 'paused');

      if (active.length === 0) {
        setErr("Aucune campagne n'est attribuée à votre compte. Contactez votre administrateur.");
        localStorage.removeItem('agent_token');
        localStorage.removeItem('agent_user');
        setLoading(false);
        return;
      }

      if (active.length === 1) {
        onLogin(res.user, active[0]);
        return;
      }

      setAuthedUser(res.user);
      setCampaigns(active);
      setSelectedId(String(active[0].id));
      setStep('campaign');
      setLoading(false);
    } catch (err) { setErr(err.error || err.message || 'Échec de la connexion'); setLoading(false); }
  };

  const submitCampaign = e => {
    e.preventDefault();
    const campaign = campaigns.find(c => c.id === parseInt(selectedId));
    if (!campaign) { setErr('Veuillez sélectionner une campagne'); return; }
    onLogin(authedUser, campaign);
  };

  const back = () => {
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_user');
    setAuthedUser(null);
    setCampaigns([]);
    setErr('');
    setStep('credentials');
  };

  return (
    <div className="login-screen">
      <div style={{ width: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={logo} alt="MynxDialer" style={{
            width: 72, height: 72, objectFit: 'contain', margin: '0 auto 16px', display: 'block'
          }} />
          <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 4, letterSpacing: '-1px', color: 'var(--text)' }}>MynxDialer</h1>
          <p style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 500 }}>
            {step === 'credentials' ? 'Portail agent — Connexion' : `Bienvenue, ${authedUser?.full_name || authedUser?.username} — Choisissez une campagne`}
          </p>
        </div>
        <div className="login-card">
          {err && (
            <div style={{ padding: '12px 16px', background: 'rgba(255,95,109,0.08)', border: '2px solid rgba(255,95,109,0.25)', borderRadius: 'var(--r)', color: 'var(--coral)', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
              {err}
            </div>
          )}

          {step === 'credentials' ? (
            <form onSubmit={submitCredentials}>
              <div style={{ marginBottom: 14 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>Nom d'utilisateur</div>
                <input className="inp inp-lg" value={u} onChange={e => setU(e.target.value)} autoFocus required />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>Mot de passe</div>
                <input className="inp inp-lg" type="password" value={p} onChange={e => setP(e.target.value)} required />
              </div>
              <button className="btn btn-cyan btn-xl" style={{ width: '100%', height: 48, fontSize: 14 }} disabled={loading}>
                {loading ? 'Connexion…' : '→ Se connecter'}
              </button>
            </form>
          ) : (
            <form onSubmit={submitCampaign}>
              <div style={{ marginBottom: 24 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>Campagne</div>
                <select className="inp inp-lg sel" value={selectedId} onChange={e => setSelectedId(e.target.value)} autoFocus required>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
                  Seules les campagnes que votre administrateur vous a attribuées sont affichées ici.
                </p>
              </div>
              <button className="btn btn-cyan btn-xl" style={{ width: '100%', height: 48, fontSize: 14, marginBottom: 10 }}>
                → Entrer dans le tableau de bord
              </button>
              <button type="button" className="btn btn-ghost" style={{ width: '100%', height: 38, fontSize: 12 }} onClick={back}>
                ← Retour à la connexion
              </button>
            </form>
          )}
        </div>
        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
          Réservé aux agents — Panneau d'administration au port 3000
        </p>
        <p style={{ textAlign: 'center', marginTop: 10, fontSize: 11, letterSpacing: '.4px', opacity: 0.6 }}>
          Propulsé par <strong>Automynx</strong>
        </p>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────────
import SuspensionGate from './components/SuspensionGate';

export default function App() {
  const [user, setUser]   = useState(() => { try { return JSON.parse(localStorage.getItem('agent_user')); } catch { return null; } });
  const [selectedCampaign, setSelectedCampaign] = useState(() => {
    try { return JSON.parse(localStorage.getItem('agent_campaign')); } catch { return null; }
  });
  const [agentStatus, setAgentStatus]       = useState('offline');
  const [pauseCodes, setPauseCodes]         = useState([]);
  const [dispositions, setDispositions]     = useState([]);
  // Active advisors for the interactive calling script (RDV step + calendar links).
  const [advisors, setAdvisors]             = useState([]);
  const [sipConfig, setSipConfig]           = useState(null);
  const [sipStatus, setSipStatus]           = useState('disconnected');
  const [callState, setCallState]           = useState(null);
  const [currentLead, setCurrentLead]       = useState(null);
  const [liveDialing, setLiveDialing]       = useState([]);
  const [showDispoModal, setShowDispoModal] = useState(false);
  // ── Fiche précédente (Karim 2026-10-22 : « agents losing fiche data mid-fill »)
  // The 120s wrap-up cap reaper (backend REAP_WRAPUP_SECONDS) frees the agent
  // and used to WIPE the whole screen — including a half-filled disposition.
  // Instead: snapshot the reaped fiche; « Fiche précédente » (TopBar) reopens it
  // and the LATE disposition is accepted server-side (POST /calls/:id/disposition
  // overwrites the reaper's NODISPO — verified in calls.js:128, no rejection).
  const [prevFiche, setPrevFiche]         = useState(null); // { callId, lead, endedAt }
  const prevFicheRef = useRef(null);
  useEffect(() => { prevFicheRef.current = prevFiche; }, [prevFiche]);
  // Draft of the disposition modal ({ callId, selected, notes, callbackAt }) —
  // captured live by the modal's onDraftChange so closing/reopening (voluntary
  // OR via the reap) never destroys typed data. Keyed by call id: a new call
  // starts a fresh draft.
  const [dispoDraft, setDispoDraft]       = useState(null);
  // True while the open disposition modal targets the PREVIOUS (reaped) fiche
  // instead of the live callState — drives onSubmit wiring + modal header.
  const [dispoMode, setDispoMode]         = useState('live'); // 'live' | 'prev'
  // « Historique » slide-in (Karim 2026-10-22 : agents must see their calling
  // history and search leads they called) — TopBar trigger.
  const [showHistory, setShowHistory]     = useState(false);
  // True once the CallFlow script completes a real RDV booking (finish('rdv') saved).
  // Drives the DispositionModal APPT guard: APPT without this asks for confirmation.
  const [rdvBooked, setRdvBooked]           = useState(false);
  const [isMuted, setIsMuted]               = useState(false);
  const [isOnHold, setIsOnHold]             = useState(false);
  const [incomingSession, setIncomingSession] = useState(null);
  const [pauseStart, setPauseStart]         = useState(null);
  // Ref twin for reconnect handlers (their closure would see a stale
  // pauseStart otherwise — same pattern as agentStatusRef below).
  const pauseStartRef = useRef(null);
  useEffect(() => { pauseStartRef.current = pauseStart; }, [pauseStart]);
  const [callbackRefresh, setCallbackRefresh] = useState(0);
  const [roomConnected, setRoomConnected]   = useState(false); // agent's *88 conference leg is up
  const roomConnectedRef = useRef(false); // live mirror for socket-reconnect handler (stale-closure safe)
  useEffect(() => { roomConnectedRef.current = roomConnected; }, [roomConnected]);
  const [breakNote, setBreakNote]           = useState(null);  // free-text reason when the agent picks "Custom"
  // Draggable split between the prospect-data zone (top) and the Talk Track
  // script (bottom). talkWidth is now the script's pixel HEIGHT.
  const [talkWidth, setTalkWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('talkTrackWidth'), 10);
    const def = Math.round((window.innerHeight || 800) * 0.6); // script = 3/5 of screen
    return (v >= 180 && v <= 1400) ? v : def;
  });
  const startTalkResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = talkWidth;
    const onMove = (ev) => {
      // Handle sits on the script's TOP edge, so dragging up grows it.
      const h = Math.max(180, Math.min(1200, startH + (startY - ev.clientY)));
      setTalkWidth(h);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setTalkWidth(w => { localStorage.setItem('talkTrackWidth', String(w)); return w; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  };
  const audioRef = useRef(null);
  const lastJoinRef = useRef(0); // throttle watchdog rejoins
  const agentStatusRef = useRef(agentStatus);
  useEffect(() => { agentStatusRef.current = agentStatus; }, [agentStatus]);
  // Live mirror of callState for the SIP callbacks (registered once, so the
  // state in their closure goes stale) — same pattern as agentStatusRef.
  const callStateRef = useRef(null);
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  // Live mirror of currentLead — the reaped handler (registered once) must read
  // the CURRENT lead, not the stale closure copy (same pattern as callStateRef).
  const currentLeadRef = useRef(null);
  useEffect(() => { currentLeadRef.current = currentLead; }, [currentLead]);

  const handleLogin = useCallback(async (u, campaign) => {
    // Start the keep-alive AudioContext from the login button gesture so
    // Chrome doesn't throttle/hibernate the tab. If the tab is throttled,
    // JsSIP's WebSocket keepalives stop, nginx times out the connection, and
    // Asterisk loses the SIP registration — calls can no longer reach the agent.
    initKeepAlive();
    setUser(u);
    const camp = campaign || null;
    setSelectedCampaign(camp);
    if (camp) localStorage.setItem('agent_campaign', JSON.stringify(camp));
    // Logged in but not yet taking calls — agent becomes "available" (and
    // eligible for the dialer) only after clicking "Start Calls".
    setAgentStatus('online');

    // Login to ALL assigned campaigns (multi-campaign support)
    try {
      const assigned = await agentAPI.getAssignedCampaigns(u.id);
      for (const camp of assigned) {
        await agentAPI.loginCampaign(u.id, camp.id).catch(() => {});
      }
    } catch (err) {
      console.warn('[App] Failed to login to all assigned campaigns:', err);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try { await authAPI.logout(); } catch (_) {}
    stopSIP(); disconnectSocket();
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_user');
    localStorage.removeItem('agent_campaign');
    setUser(null); setCallState(null); setCurrentLead(null); setSelectedCampaign(null);
    setSipConfig(null); setSipStatus('disconnected'); setAgentStatus('offline');
    setPrevFiche(null); setDispoDraft(null); setDispoMode('live');
  }, []);

  // Initialize after login
  useEffect(() => {
    if (!user) return;

    adminAPI.pauseCodes().then(setPauseCodes).catch(() => {});
    advisorsAPI.list().then(setAdvisors).catch(() => {});

    // Request mic permission upfront so browser is ready when a call arrives
    requestMicPermission().then(granted => {
      if (!granted) console.warn('[App] No mic permission — audio will not work');
    });

    // SIP
    sipAPI.getConfig().then(config => {
      // Whenever the page itself is https, route the SIP WebSocket through
      // the SAME origin's /ws. nginx terminates TLS with the very cert that
      // just served this page (raw-IP deployments use a self-signed cert the
      // user already accepted), so this works for domains AND raw IPs. The
      // backend-provided raw ws://IP:8088 would be blocked as mixed content
      // on an https page -> agent stuck "REGISTERING".
      if (window.location.protocol === 'https:') {
        config = { ...config, ws_servers: `wss://${window.location.host}/ws` };
      }
      setSipConfig(config);
      setSipStatus('registering');
      initSIP(config, {
        onRegistered:        () => setSipStatus('registered'),
        onUnregistered:      () => setSipStatus('disconnected'),
        onRegistrationFailed:() => setSipStatus('failed'),
        onIncoming: (session, num, name) => {
          // Auto-answer calls from the dialer system (Asterisk bridging a lead).
          // In a predictive dialer, ALL incoming SIP calls come from the system.
          // Agents only make outbound calls manually; they never receive personal calls here.
          const isSystemCall = !num || num === '' || num === 'anonymous' ||
            name?.toLowerCase().includes('dialer') || name?.toLowerCase().includes('system') ||
            name?.toLowerCase().includes('blu') ||
            /^\d{7,}$/.test(num); // any 7+ digit number is a lead being bridged

          if (isSystemCall) {
            // Auto-answer: this is Asterisk bridging us to a lead (or audio check)
            console.log('[SIP] Auto-answering system call from:', num, name);
            answer(session);
            setCallState(prev => ({ ...(prev || {}), status: 'answered', answered_at: new Date().toISOString(), caller_id: num }));
            setAgentStatus('oncall');
          } else {
            // Manual incoming call — show UI for agent to answer
            setIncomingSession(session);
            setCallState(prev => ({ ...(prev || {}), incoming: true, caller_id: num, caller_name: name, status: 'ringing' }));
          }
        },
        onCallConfirmed: (session) => {
          // The room call (*88) is the agent's persistent audio leg — it must NOT
          // drive the lead UI (which is driven by socket events). Just mark the
          // room connected so we know the agent's audio is ready for leads.
          if (session?._isRoom) { setRoomConnected(true); return; }
          const now = new Date().toISOString();
          setCallState(prev => ({ ...(prev || {}), status: 'answered', answered_at: now }));
          setAgentStatus('oncall');
        },
        onCallEnded: (cause, session) => {
          setIncomingSession(null);
          setIsMuted(false);
          setIsOnHold(false);
          // Room leg ended (agent toggled OFFLINE, or it dropped). Just clear the
          // flag — do NOT auto-rejoin (that loop spammed "conference is locked" and
          // piled up stale room sessions). The agent re-joins via the ONLINE toggle.
          if (session?._isRoom) { setRoomConnected(false); return; }
          // For manual calls (no DB call ID), clear state entirely.
          // For autodialer calls, preserve callState so disposition modal can show.
          // ended_at = the DISPO/wrap-up timer's start point (without it the
          // timer ran with start=null and froze at 00:00:00 through wrap-up).
          setCallState(prev => prev?.id ? { ...prev, status: 'ended', ended_at: new Date().toISOString() } : null);
          // A logged call that just ended = WRAP-UP (disposition pending), not
          // 'available' — otherwise the dialer grabs the agent mid-wrap-up.
          // callStateRef is live (the closure here is not). Unlogged manual
          // calls have nothing to disposition → straight back to available.
          const hadCall = !!callStateRef.current?.id;
          setAgentStatus(prev => prev === 'oncall' ? (hadCall ? 'wrapup' : 'available') : prev);
          if (hadCall) setShowDispoModal(true);
        },
        onCallFailed: (cause, session) => {
          console.log('[App] Call failed:', cause);
          setIncomingSession(null);
          if (session?._isRoom) { setRoomConnected(false); return; }
          setCallState(null);
          setAgentStatus(prev => prev === 'oncall' ? 'available' : prev);
        },
        onRemoteStream: (stream) => {
          // Always attach — for the conference flow this stream is the room mix
          // (silence while waiting, the lead's voice once they're bridged in).
          const tracks = stream?.getAudioTracks?.() || [];
          console.log('[SIP] Remote stream — audio tracks:', tracks.length, tracks.map(t => t.readyState));
          attachRemoteAudio(stream, audioRef.current);
        },
        onOutgoing: (session) => {
          // The room call going out must stay invisible to the lead UI — the agent
          // shows "waiting for lead", not "on call", until a lead is socket-assigned.
          if (session?._isRoom) return;
          // Manual outbound call — show ringing UI immediately (flagged manual so
          // hangup targets this browser leg, not the room / a DB lead call).
          const dialed = session?.remote_identity?.uri?.user || '';
          setCallState(prev => ({ ...(prev || {}), status: 'ringing', outbound: true, manual: true, caller_id: dialed }));
          setAgentStatus('oncall');
          const digits = dialed.replace(/\D/g, '');
          if (digits.length < 7) return;
          // Show the lead sheet if the number is in our (account-scoped) data, then
          // log a call record so this manual call gets full hangup & disposition.
          const finish = (match) => {
            if (match) setCurrentLead(match);
            callsAPI.manual({ phone: digits, lead_id: match ? match.id : undefined })
              .then(r => {
                if (r && r.call_id) setCallState(prev => (prev && prev.manual)
                  ? { ...prev, id: r.call_id, lead_id: match ? match.id : null } : prev);
              })
              .catch(() => {});
          };
          leadsAPI.search(digits).then(res => {
            const list = res?.leads || res?.data?.leads || [];
            const match = list.find(l => {
              const p = (l.phone || '').replace(/\D/g, '');
              return p && (p.endsWith(digits) || digits.endsWith(p));
            }) || null;
            finish(match);
          }).catch(() => finish(null));
        },
        onMuted: (m) => setIsMuted(m),
        onHold:  (h) => setIsOnHold(h)
      });
    }).catch(err => { console.warn('[SIP] Not configured:', err.error || err.message); setSipStatus('failed'); });

    // Socket
    const socket = getSocket();

    socket.on('call:assigned', (data) => {
      // Don't downgrade an already-answered call back to ringing if events arrive out of order
      setCallState(prev => {
        if (prev?.status === 'answered' || prev?.status === 'ended') {
          return { ...prev, id: data.call_id, lead_id: data.lead_id, caller_id: data.caller_id || prev.caller_id };
        }
        return { id: data.call_id, lead_id: data.lead_id, caller_id: data.caller_id, status: 'ringing', incoming: false };
      });
      if (data.lead) setCurrentLead(data.lead);
      setRdvBooked(false); // new call → guard re-arms
      setLiveDialing(prev => [...prev.filter(x => x.id !== data.call_id), { id: data.call_id, phone: data.lead?.phone || data.caller_id, status: 'ringing', startedAt: new Date().toISOString() }]);
    });

    socket.on('call:answered', (data) => {
      // Always create callState even if call:assigned was missed — ensures timer starts
      setCallState(prev => ({
        ...(prev || { id: data.call_id, incoming: false }),
        status: 'answered',
        answered_at: new Date().toISOString()
      }));
      setAgentStatus('oncall');
      setLiveDialing(prev => prev.map(x => x.id === data.call_id ? { ...x, status: 'answered' } : x));
    });

    socket.on('call:hangup', (data) => {
      setIsMuted(false); setIsOnHold(false);
      setLiveDialing(prev => prev.filter(x => x.id !== data.call_id));
      if (data.reaped) {
        // Wrap-up cap hit (120s reaper). The old code WIPED everything mid-fill —
        // the exact complaint (2026-10-22): fiche + typed disposition destroyed
        // while the agent was still working. The backend still accepts a LATE
        // disposition, so: snapshot the fiche, keep the draft, free the agent
        // (backend already did) — the agent finishes via « Fiche précédente ».
        const cs = callStateRef.current;
        const cl = currentLeadRef.current;
        const sameCall = data.call_id && cs && data.call_id === cs.id;
        if (data.call_id && (!cs || cs.status === 'ended' || sameCall)) {
          setPrevFiche(prev => prev || {
            callId: data.call_id,
            // Only trust the on-screen lead when the reap targets THAT call —
            // after a reload (cs null) the lead is re-fetched on reopen.
            lead: sameCall ? cl : null,
            endedAt: (sameCall && cs?.ended_at) || new Date().toISOString(),
          });
        }
        // Only tear down the live screen if this reap targets OUR current call —
        // a stale reap for an older call must never kill a live one.
        const liveCall = cs && (cs.status === 'ringing' || cs.status === 'answered');
        if (!liveCall || sameCall) {
          setCallState(null); setCurrentLead(null); setAgentStatus('available');
          setShowDispoModal(false);
        }
        return;
      }
      // Customer hung up (or wrap-up restored after a socket flap / browser
      // reload — data.restored). Mark the call ended and prompt a disposition.
      // Enter WRAP-UP, never 'available': the dialer must not grab the next
      // lead before this one is dispositioned (that race caused the button
      // flicker and leads piling up). Becomes 'available' on disposition submit
      // (the backend reaper is the 40s safety net).
      // If callState is missing (fresh reload — the backend self-heal re-emits
      // this event), build it from the event so the modal has a call id to
      // disposition.
      setCallState(prev => prev
        ? { ...prev, status: 'ended', ended_at: new Date().toISOString() }
        : (data.call_id ? { id: data.call_id, status: 'ended', ended_at: new Date().toISOString() } : prev));
      if (data.needs_disposition) {
        // Deux temps (Karim, 2026-10-19) : PAS d'ouverture automatique de la
        // modale — l'agent reste en wrap-up avec le script ouvert pour
        // compléter la réservation (RDV), puis clique « DISPOSITION » quand
        // il est prêt. Le barrage 409 APPT reste le garde-fou serveur.
        setAgentStatus('wrapup');
      }
    });

    socket.on('call:started', (data) => {
      setLiveDialing(prev => [...prev.filter(x => x.id !== data.call_id), { id: data.call_id, phone: data.phone, status: 'dialing', startedAt: new Date().toISOString() }]);
    });

    socket.on('lead:assigned', (data) => setCurrentLead(data.lead));

    // Admin edited/added/removed a client (advisor) — the list agents script
    // against is cached from login, so re-fetch it live instead of forcing a
    // browser reload (Karim 2026-10-16). Renames, calendar links, new clients,
    // deactivations — all arrive within a second of the admin's save.
    socket.on('advisors:refresh', () => {
      advisorsAPI.list().then(setAdvisors).catch(() => {});
    });

    socket.on('force:logout', () => {
      alert('Vous avez été déconnecté par un administrateur.');
      handleLogout();
    });

    return () => {
      socket.off('call:assigned'); socket.off('call:answered'); socket.off('call:hangup');
      socket.off('call:started'); socket.off('lead:assigned'); socket.off('advisors:refresh');
    };
  }, [user]);

  // Load dispositions when campaign changes + register initial session
  useEffect(() => {
    if (!selectedCampaign || !user) return;
    adminAPI.dispositions(selectedCampaign.id).then(setDispositions).catch(() => {});
    // Karim 2026-10-15: agents are READY (available) BY DEFAULT the moment they
    // enter the campaign — no extra click. "Start Calls" only joins the audio
    // room. A paused agent stays paused (resumes via their own click).
    (async () => {
      await agentAPI.loginCampaign(user.id, selectedCampaign.id).catch(() => {});
      // Wrap-up restore (2026-10-15): if the browser reloaded while a call was
      // undispositioned, loginCampaign just stamped a fresh 'online' session —
      // asserting 'available' here would let the dialer claim the agent and
      // dispatch the next lead while the previous call has no disposition.
      // Ask the backend for the pending call; if there is one, restore WRAP-UP
      // (badge + disposition modal) instead. The backend's 3s socket self-heal
      // re-emits 'call:hangup' too — this check makes the restore deterministic
      // (no claim-race window).
      const pending = await callsAPI.pendingDispo().catch(() => null);
      if (pending && pending.id) {
        console.log('[App] Restoring wrap-up after reload — pending call', pending.id);
        // Wrap-up restauré SANS ouvrir la modale (Karim, 2026-10-19) : le bouton
        // « DISPOSITION » est là (appel terminé) — l'agent finit le script d'abord.
        setCallState({ id: pending.id, status: 'ended', ended_at: pending.hung_up_at, caller_id: pending.caller_id });
        await handleStatusChange('wrapup', null);
        return;
      }
      if (agentStatusRef.current !== 'paused' && agentStatusRef.current !== 'oncall' && agentStatusRef.current !== 'wrapup') {
        await handleStatusChange('available', null);
      }
    })();

    // Restore session after socket reconnect (e.g. backend restart, network blip).
    // The backend keeps the session row open through disconnects (90s grace,
    // no session kill), so this just refreshes it and restores the exact
    // pre-blip status — 'paused' included (loginCampaign alone leaves the user
    // 'online' in the DB, desynced from the UI's paused state).
    const socket = getSocket();
    const handleReconnect = async () => {
      const prevStatus = agentStatusRef.current;
      // While on a call the AMI Hangup handler needs the existing session intact —
      // calling loginCampaign here would close it and break status reset on hangup.
      // Same in WRAP-UP: the session row is what the disposition route frees from
      // 'wrapup', and the backend self-heal re-emits the lost 'call:hangup' using
      // it. Keep it; restore nothing (the agent's wrap-up state survives the flap).
      if (prevStatus === 'oncall' || prevStatus === 'wrapup') return;
      console.log('[Socket] Reconnected — restoring agent sessions for all campaigns');
      try {
        const assigned = await agentAPI.getAssignedCampaigns(user.id);
        for (const camp of assigned) {
          await agentAPI.loginCampaign(user.id, camp.id).catch(() => {});
        }
      } catch (err) {
        console.warn('[App] Failed to restore sessions on reconnect:', err);
      }
      if (prevStatus === 'available' || prevStatus === 'paused') {
        await agentAPI.setStatus(user.id, prevStatus, null, selectedCampaign.id).catch(() => {});
        setAgentStatus(prevStatus);
        // Pause timer survived a socket flap: the pause may have started
        // BEFORE the disconnect — if the start timestamp was lost (state reset
        // by a reload) resume the clock instead of freezing it at 00:00:00.
        if (prevStatus === 'paused' && !pauseStartRef.current) {
          setPauseStart(new Date().toISOString());
        }
      }
      // A socket.io flap does NOT touch the SIP/WebRTC leg (separate /ws
      // connection to Asterisk) — so NEVER tear the room down here. The old
      // "leaveRoom+joinRoom on every reconnect" killed the fresh leg ~400ms
      // after Answer whenever the socket flapped (2026-10-13: supervisor on a
      // flapping VPN got no greeting/no audio; stable-network agents were fine).
      // If the leg is actually dead, the media-stall watchdog rejoins it.
      if (sipConfig && !roomConnectedRef.current) {
        try { joinRoom(sipConfig); } catch (_) {}
      }
    };
    socket.on('reconnect', handleReconnect);
    return () => socket.off('reconnect', handleReconnect);
  }, [selectedCampaign, user]);

  // Recover the lead sheet if 'call:assigned' was missed (socket mid-reconnect):
  // a call can be answered with the lead data event dropped, leaving the sheet
  // blank. When answered but no lead is shown, fetch the active call's lead so
  // the agent always sees the customer's data. The 1.5s delay lets the normal
  // event arrive first; the currentLead dep cancels the fetch if it does.
  useEffect(() => {
    if (callState?.status !== 'answered' || !callState?.id || currentLead) return;
    const callId = callState.id;
    const t = setTimeout(async () => {
      try {
        const active = await callsAPI.active();
        const list = Array.isArray(active) ? active : (active?.data || []);
        const mine = list.find(c => c.id === callId);
        if (mine?.lead_id) {
          const lead = await leadsAPI.get(mine.lead_id);
          setCurrentLead(prev => prev || lead);
        }
      } catch (_) { /* best-effort recovery */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [callState?.status, callState?.id, currentLead]);

  // Watchdog: keep the agent's conference-room leg alive AND audible. Two checks:
  //  (a) room leg missing entirely → rejoin (original);
  //  (b) MEDIA-STALL — the leg looks alive (SIP confirmed) but its inbound audio
  //      flatlined. An idle room always streams hold music, so no new inbound
  //      audio packets for 15s while idle means a stale ICE/DTLS path (the
  //      2026-10-13 "prospect can't hear agent" bug: agent3's 21-min-old leg).
  //      Force a clean rejoin so the NEXT call has working audio. Skipped while
  //      oncall (the room leg carries the live call — never touch it mid-call).
  const lastRxRef = useRef({ pkts: null, since: 0 });
  useEffect(() => {
    if (!user) return;
    const wd = setInterval(() => {
      const st = agentStatusRef.current;
      // 'wrapup' included: the agent stays in their room through wrap-up — the
      // room leg is the audio path for the NEXT call and must not be rejoined
      // needlessly mid-wrap-up (same reason as 'oncall').
      const shouldBeInRoom = st === 'online' || st === 'available' || st === 'oncall' || st === 'wrapup';
      if (shouldBeInRoom && !roomConnected && sipConfig && Date.now() - lastJoinRef.current > 7000) {
        console.log('[App] Watchdog: room leg missing — rejoining');
        lastJoinRef.current = Date.now();
        try { joinRoom(sipConfig); } catch (_) {}
        return;
      }
      // (b) media-stall detection — only when idle (MOH should be streaming).
      if (shouldBeInRoom && roomConnected && st !== 'oncall') {
        getRoomInboundPackets().then(pkts => {
          if (pkts == null) return;
          const now = Date.now();
          if (lastRxRef.current.pkts === pkts) {
            if (now - lastRxRef.current.since > 15000 && now - lastJoinRef.current > 30000) {
              console.log('[App] Watchdog: room media stalled (no inbound audio 15s) — forcing rejoin');
              lastJoinRef.current = now;
              lastRxRef.current = { pkts: null, since: 0 };
              try { leaveRoom(); } catch (_) {}
              try { joinRoom(sipConfig); } catch (_) {}
            }
          } else {
            lastRxRef.current = { pkts, since: now };
          }
        }).catch(() => {});
      }
      const a = audioRef.current;
      if (a && a.paused && a.srcObject) a.play().catch(() => {});
    }, 5000);
    return () => clearInterval(wd);
  }, [user, roomConnected, sipConfig]);

  // Auto-expire stale entries from the Live Dialing ticker. Predictive calls that
  // didn't connect to THIS agent never send a removal event, so prune in-flight
  // (dialing/ringing) rows older than 20s. The agent's own connected call shows as
  // 'answered' and is removed by call:hangup, so it's left alone here.
  useEffect(() => {
    const iv = setInterval(() => {
      const cutoff = Date.now() - 20000;
      setLiveDialing(prev => {
        const next = prev.filter(x => !(['dialing', 'ringing'].includes(x.status) && new Date(x.startedAt).getTime() < cutoff));
        return next.length === prev.length ? prev : next;
      });
    }, 4000);
    return () => clearInterval(iv);
  }, []);

  const handleStatusChange = async (status, pcId, note) => {
    setAgentStatus(status);
    if (status === 'paused') { setPauseStart(new Date().toISOString()); setBreakNote(note || null); }
    else { setPauseStart(null); setBreakNote(null); }
    try {
      await agentAPI.setStatus(user.id, status, pcId, selectedCampaign?.id, note);
      getSocket().emit('agent:status', { status, pause_code_id: pcId, note: note || null });
      // Update status for all active campaign sessions
      const assigned = await agentAPI.getAssignedCampaigns(user.id);
      for (const camp of assigned) {
        await agentAPI.loginCampaign(user.id, camp.id).catch(() => {});
      }
    } catch (err) { console.error(err); }
  };

  // Toggles whether the agent is connected to live auto-dial calls.
  // This is the ONLY thing that makes the agent eligible for the dialer
  // (status = 'available'). It no longer touches presence or the audio check.
  const handleStartCalls = async () => {
    const goingLive = agentStatus !== 'available';
    if (goingLive) {
      // Keep the tab active so Chrome doesn't hibernate it and miss SIP INVITEs
      initKeepAlive();
      // You must be in your conference room to receive leads. If you started calls
      // without going ONLINE first, join the room now (auto go-online).
      if (!roomConnected) {
        lastJoinRef.current = Date.now();
        try { joinRoom(sipConfig); } catch (e) { console.warn('[App] joinRoom failed:', e.message); }
      }
      // Recreate the campaign session so claimAgent() finds an agent_sessions row.
      // Login to ALL assigned campaigns for multi-campaign support
      try {
        const assigned = await agentAPI.getAssignedCampaigns(user.id);
        for (const camp of assigned) {
          await agentAPI.loginCampaign(user.id, camp.id).catch(() => {});
        }
      } catch (err) {
        console.warn('[App] Failed to login to all campaigns:', err);
      }
      // status='available' is what makes the dialer start dispatching leads.
      await handleStatusChange('available', null);
    } else {
      // Stop Calls — stop receiving autodial leads but STAY in the room (online).
      // Clear any leftover ended-call so a finished lead doesn't linger on screen.
      if (!callState || callState.status === 'ended') {
        setCallState(null); setCurrentLead(null); setShowDispoModal(false);
      }
      await handleStatusChange('online', null);
    }
  };

  // Online/Offline presence toggle — independent of live-call connection.
  // Going back online re-establishes the campaign session (offline closes it).
  // ONLINE/OFFLINE = in/out of the conference room. ONLINE joins the agent's room
  // (they hear "you are the only person" then hold music while waiting); OFFLINE
  // leaves it entirely. Going available for autodial (Start Calls) requires ONLINE.
  const handleToggleOnline = async () => {
    if (agentStatus === 'offline') {
      initKeepAlive();
      // Login to ALL assigned campaigns
      try {
        const assigned = await agentAPI.getAssignedCampaigns(user.id);
        for (const camp of assigned) {
          await agentAPI.loginCampaign(user.id, camp.id).catch(() => {});
        }
      } catch (err) {
        console.warn('[App] Failed to login to campaigns on online:', err);
      }
      if (!roomConnected) {
        lastJoinRef.current = Date.now();
        try { joinRoom(sipConfig); } catch (e) { console.warn('[App] joinRoom failed:', e.message); }
      }
      await handleStatusChange('online', null);
    } else {
      // Leaving — drop out of autodial AND leave the room.
      try { leaveRoom(); } catch (_) {}
      setRoomConnected(false);
      await handleStatusChange('offline', null);
    }
  };

  // Manually trigger the "you are the only person in this conference" audio
  // check — fully decoupled from Start Calls so agents can test audio anytime.
  const handleAudioCheck = async () => {
    try { await agentAPI.audioCheck(user.id); } catch (_) {}
  };

  const handleResume = async () => {
    // Login to ALL assigned campaigns on resume
    try {
      const assigned = await agentAPI.getAssignedCampaigns(user.id);
      for (const camp of assigned) {
        await agentAPI.loginCampaign(user.id, camp.id).catch(() => {});
      }
    } catch (err) {
      console.warn('[App] Failed to resume campaigns:', err);
    }
    handleStatusChange('available', null);
  };

  // ── Deux temps (Karim, 2026-10-19) ──────────────────────────────────────────
  // 1. En conversation : « RACCROCHER » — termine l'appel CÔTÉ TRUNK (la jambe
  //    lead, celle que le backend coupe via AMI), l'agent passe en wrap-up et
  //    le script reste ouvert pour compléter/réserver le RDV.
  // 2. Appel terminé : le même bouton devient « DISPOSITION » — ouvre la modale.
  // L'ancien « Raccrocher & Dispo » en un clic permettait de disposer sans
  // remplir le script (appointments perdus) ; le barrage 409 APPT reste le
  // garde-fou serveur.
  const handleHangupOnly = async () => {
    // Manual call whose record failed to log → just hang up (nothing to dispo).
    if (callState?.manual && !callState.id) { handleHangup(); return; }
    if (callState && callState.status !== 'ended') {
      if (callState.manual) {
        hangupManual();
      } else {
        if (!roomConnected) hangup();
        if (callState.id) await callsAPI.hangup(callState.id).catch(() => {});
      }
      setCallState(prev => prev ? { ...prev, status: 'ended', ended_at: new Date().toISOString() } : prev);
      setIsMuted(false); setIsOnHold(false);
      // Agent chose to end it — same WRAP-UP state as a customer hangup (the
      // backend hangup route mirrors this, guarded on 'oncall').
      setAgentStatus('wrapup');
    }
  };

  const handleOpenDispo = () => { setDispoMode('live'); setShowDispoModal(true); };

  // ── Fiche précédente (2026-10-22) ────────────────────────────────────────────
  // Reopen the REAPED fiche for its LATE disposition. If the lead wasn't
  // captured at reap time (browser reloaded), look the call up so the agent
  // still sees the full name + phone (Karim permanent rule) in the modal header.
  const dispoModeRef = useRef('live');
  useEffect(() => { dispoModeRef.current = dispoMode; }, [dispoMode]);

  const openPrevFiche = useCallback(async () => {
    const pf = prevFicheRef.current;
    if (!pf) return;
    if (!pf.lead && pf.callId) {
      try {
        const d = await callsAPI.list({ agent_id: user?.id, date_from: new Date().toISOString().slice(0, 10), limit: 200 });
        const row = (d?.calls || []).find(c => c.id === pf.callId);
        if (row?.lead_id) {
          const lead = await leadsAPI.get(row.lead_id);
          setPrevFiche(p => p ? { ...p, lead } : p);
        }
      } catch (_) { /* reopen without the header — disposition still works */ }
    }
    setDispoMode('prev');
    setShowDispoModal(true);
  }, [user?.id]);

  // Live draft sync from the disposition modal — stable identity (useCallback,
  // reads via refs) so the modal's draft effect never loops on re-renders.
  const handleDispoDraftChange = useCallback((d) => {
    setDispoDraft(prev => {
      const callId = dispoModeRef.current === 'prev'
        ? prevFicheRef.current?.callId
        : callStateRef.current?.id;
      if (prev && prev.callId === callId && prev.selected?.id === d.selected?.id
          && prev.notes === d.notes && prev.callbackAt === d.callbackAt) return prev;
      return { callId, selected: d.selected, notes: d.notes, callbackAt: d.callbackAt };
    });
  }, []);

  // Contexte : en conversation → raccrocher ; appel terminé → disposition.
  // C'est CE handler que le bouton deux-états (TopBar) et F4 appellent.
  const handleCallControl = async () => {
    if (callState && callState.status !== 'ended') return handleHangupOnly();
    if (callState?.status === 'ended') return handleOpenDispo();
  };

  const handleDispositionSubmit = async (dispId, notes, callbackAt, pauseCodeId, targetCallId) => {
    // ── LATE disposition of a REAPED fiche (2026-10-22) ─────────────────────────
    // The call is long dead (wrap-up cap reaped it): no hangup, no live-state
    // cleanup, no status change — the agent may already be on the NEXT call.
    // The backend overwrites the reaper's NODISPO stamp (no rejection —
    // verified calls.js:128) and re-runs the full lead/bookkeeping path.
    if (targetCallId) {
      try {
        await callsAPI.disposition(targetCallId, { disposition_id: dispId, notes, callback_at: callbackAt });
      } catch (err) {
        if (err?.error === 'APPT_REQUIRES_RDV') throw err;
        console.error(err);
      }
      setPrevFiche(null); setDispoDraft(null);
      setShowDispoModal(false);
      if (callbackAt) setCallbackRefresh(n => n + 1);
      return;
    }
    const callId = callState?.id;
    try {
      if (callId) {
        await callsAPI.disposition(callId, { disposition_id: dispId, notes, callback_at: callbackAt });
        if (callState.manual) {
          // Manual call: end the browser leg; the disposition already stamped
          // hung_up_at on the record. Never touch the room (*88) leg.
          hangupManual();
        } else {
          // Conference mode: the SIP session IS the agent's room — never terminate it.
          // The backend hangs up the lead's leg; the agent stays for the next lead.
          if (!roomConnected) hangup();
          await callsAPI.hangup(callId).catch(() => {});
        }
      }
    } catch (err) {
      // Barrage serveur (409 APPT_REQUIRES_RDV, 2026-10-16) : ne RIEN nettoyer
      // — l'appel et la modale restent ouverts, l'agent voit le message du
      // serveur dans la modale et doit réserver le RDV ou choisir un autre
      // résultat. La disposition n'a PAS été enregistrée côté serveur.
      if (err?.error === 'APPT_REQUIRES_RDV') throw err;
      console.error(err);
    }
    // ALWAYS clean up + free the agent — even if the call id was already gone (a
    // race), never leave them stranded in wrap-up (that stalled their Live Dialing).
    setShowDispoModal(false);
    setCallState(null);
    setCurrentLead(null);
    setDispoDraft(null); // live call dispositioned — next call starts a fresh draft
    setIsMuted(false); setIsOnHold(false);
    setLiveDialing(prev => prev.filter(x => x.id !== callId));
    if (pauseCodeId) {
      await handleStatusChange('paused', pauseCodeId);
    } else {
      setAgentStatus('available');
    }
    // Trigger callback list refresh if a callback was set
    if (callbackAt) setCallbackRefresh(n => n + 1);
  };

  // The call is already hung up (Hangup & Dispo ended it). Closing the dispo (X /
  // Cancel / backdrop) just returns to the lead sheet in WRAP-UP — the agent must
  // submit a disposition to go available. They can re-open the dispo any time via
  // the Hangup & Dispo button (which skips the hang-up since the call already ended).
  const handleDispoClose = () => {
    setShowDispoModal(false);
  };

  const handlePauseNow = async (pauseCodeId) => {
    if (!roomConnected) hangup();
    if (callState?.id) await callsAPI.hangup(callState.id).catch(() => {});
    setCallState(null); setCurrentLead(null);
    setIsMuted(false); setIsOnHold(false);
    await handleStatusChange('paused', pauseCodeId || null);
  };

  const handleHangup = async () => {
    // Manual dial: terminate ONLY that browser leg via hangupManual() — never the
    // room (*88) leg. Also close the DB call record if one was logged.
    if (callState?.manual) {
      hangupManual();
      if (callState.id) await callsAPI.hangup(callState.id).catch(() => {});
      setCurrentLead(null); // clear the manual lead sheet
    } else {
      if (!roomConnected) hangup();
      if (callState?.id) await callsAPI.hangup(callState.id).catch(() => {});
    }
    setCallState(null);
    setAgentStatus('available');
    setIsMuted(false); setIsOnHold(false);
  };

  const handleAnswer = () => {
    answer(incomingSession);
    setIncomingSession(null);
    setCallState(prev => ({ ...(prev || {}), status: 'answered', answered_at: new Date().toISOString() }));
    setAgentStatus('oncall');
  };

  const handleMute = () => { mute(!isMuted); setIsMuted(m => !m); };
  const handleHold = () => { hold(!isOnHold); setIsOnHold(h => !h); };

  // ── Global F1–F4 shortcuts (the ones advertised in the LeftPanel panel) ──────
  // F1 blocks the browser help page via preventDefault (keydown on F1 is
  // cancellable — see MDN Element/keydown_event). Latest handlers kept in a ref
  // so the listener binds once and never goes stale.
  const shortcutsRef = useRef({});
  shortcutsRef.current = { callState, incomingSession, handleAnswer, handleHangup, handleMute, handleHold, handleCallControl };
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if (!['F1','F2','F3','F4'].includes(e.key)) return;
      // Never hijack keys while the agent is typing in a field
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const s = shortcutsRef.current;
      const live = s.callState && ['ringing','answered'].includes(s.callState.status);
      if (e.key === 'F1') {
        e.preventDefault();
        if (s.incomingSession) s.handleAnswer();
        else if (live) s.handleHangup();
      } else if (e.key === 'F2') {
        e.preventDefault(); if (live) s.handleMute();
      } else if (e.key === 'F3') {
        e.preventDefault(); if (live) s.handleHold();
      } else if (e.key === 'F4') {
        // Deux temps (Karim, 2026-10-19) : en conversation → RACCROCHER ;
        // appel terminé → ouvre la disposition. Même logique que le bouton.
        e.preventDefault();
        if (live || s.callState?.status === 'ended') s.handleCallControl();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [user]);

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  const hasCall = callState && ['ringing','answered'].includes(callState.status);
  // agent01 is the internal test account — it keeps every option visible.
  // All other agents get the simplified daily-task view.
  const fullUI = user?.username === 'agent01';

  // Wrap-up countdown target — MUST match the backend reaper cap
  // (REAP_WRAPUP_SECONDS, dialerEngine.js default 120; not overridden in .env).
  // At zero the reaper frees the agent + lead; the fiche survives via prevFiche.
  const WRAPUP_CAP_SECONDS = 120;
  const wrapupEndsAt = agentStatus === 'wrapup' && callState?.ended_at
    ? new Date(new Date(callState.ended_at).getTime() + WRAPUP_CAP_SECONDS * 1000).toISOString()
    : null;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', overflow: 'hidden' }}>
      <SuspensionGate role="agent" />
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

      <TopBar
        user={user}
        agentStatus={agentStatus}
        selectedCampaign={selectedCampaign}
        pauseCodes={pauseCodes}
        callState={callState}
        pauseStart={pauseStart}
        breakNote={breakNote}
        fullUI={fullUI}
        onStartCalls={handleStartCalls}
        onToggleOnline={handleToggleOnline}
        onAudioCheck={handleAudioCheck}
        onResume={handleResume}
        onHangupAndDispo={handleCallControl}
        onStatusChange={handleStatusChange}
        onLogout={handleLogout}
        wrapupEndsAt={wrapupEndsAt}
        prevFiche={prevFiche}
        onOpenPrevFiche={openPrevFiche}
        onOpenHistory={() => setShowHistory(true)}
      />

      <DialingTicker liveDialing={liveDialing} totalDialing={liveDialing.length} fullUI={fullUI} />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        <LeftPanel
          user={user}
          sipConfig={sipConfig}
          sipStatus={sipStatus}
          callState={callState}
          onHangup={handleHangup}
          fullUI={fullUI}
        />

        {/* Center column: prospect data on TOP (2/5), calling script on BOTTOM
            (3/5) — Karim 2026-10-13. The draggable divider now resizes the
            script's share vertically. */}
        {/* Center column: prospect data on TOP (2/5), calling script on BOTTOM
            (3/5) — Karim 2026-10-13. Script zone height is drag-adjustable via
            the divider (talkWidth = script height in px, default 60% viewport). */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', borderBottom: '1px solid var(--border-dim)' }}>
            <CustomerDetails
              lead={currentLead}
              callState={callState}
              campaignName={selectedCampaign?.name}
              fullUI={fullUI}
            />
          </div>
          <div
            onMouseDown={startTalkResize}
            title="Glisser pour redimensionner le Talk Track"
            style={{ height: 6, flexShrink: 0, cursor: 'row-resize', background: 'var(--border-dim)', transition: 'background 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--cyan)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--border-dim)'; }}
          />
          <div style={{ height: talkWidth, flexShrink: 0, minHeight: 0, overflow: 'auto', display: 'flex' }}>
            <CallFlow campaign={selectedCampaign} lead={currentLead} user={user} advisors={advisors} width={talkWidth} onRdvBooked={() => setRdvBooked(true)} />
          </div>
        </div>

        <div style={{ width: 240, display: 'flex', flexDirection: 'column', overflow: 'visible', flexShrink: 0, position: 'relative' }}>
          <WebPhone
            sipStatus={sipStatus}
            callState={callState}
            sipConfig={sipConfig}
            isMuted={isMuted}
            isOnHold={isOnHold}
            incomingSession={incomingSession}
            onAnswer={handleAnswer}
            onHangup={handleHangup}
            onMute={handleMute}
            onHold={handleHold}
            fullUI={fullUI}
            onDTMF={(d) => { const { sendDTMF } = require('./services/sipClient'); sendDTMF(d); }}
          />
          <CallbackList sipConfig={sipConfig} refreshTrigger={callbackRefresh} />
          <NoShowList sipConfig={sipConfig} refreshTrigger={callbackRefresh} />
        </div>
      </div>

      {showDispoModal && (
        // Fuseau du popup RAPPEL = MONTRÉAL, TOUJOURS (Karim 2026-10-15) —
        // même si la campagne en indique un autre : les agents et les
        // clients sont au Québec.
        <DispositionModal
          dispositions={dispositions}
          onSubmit={dispoMode === 'prev'
            ? (dispId, notes, cbAt, pauseCodeId) => handleDispositionSubmit(dispId, notes, cbAt, pauseCodeId, prevFiche?.callId)
            : handleDispositionSubmit}
          onPause={handlePauseNow}
          onClose={handleDispoClose}
          tz={'America/Toronto'}
          rdvBooked={rdvBooked}
          // In prev-fiche mode let the SERVER decide APPT (it checks the real
          // rendezvous rows — a RDV booked mid-call survives the reap, but the
          // client-side rdvBooked flag was reset by the next call:assigned).
          allowApptTry={dispoMode === 'prev'}
          initial={dispoDraft && dispoDraft.callId === (dispoMode === 'prev' ? prevFiche?.callId : callState?.id) ? dispoDraft : null}
          onDraftChange={handleDispoDraftChange}
          fiche={dispoMode === 'prev' && prevFiche?.lead
            ? { first_name: prevFiche.lead.first_name, last_name: prevFiche.lead.last_name, phone: prevFiche.lead.phone }
            : null}
        />
      )}

      {/* Internal team chat (rides existing socket; no call impact) */}
      <ChatWidget user={user} campaignId={selectedCampaign?.id} campaignName={selectedCampaign?.name} />

      {/* My call history + lead search (Karim 2026-10-22) */}
      {showHistory && <CallHistory user={user} onClose={() => setShowHistory(false)} />}

      {/* Appels internes (Karim 2026-10-20) — réception seulement, aucun SIP */}
      <InterCallWidget />

      {/* Persistent brand watermark — stays on the dialer at all times. */}
      <img src={logo} alt="" aria-hidden="true" style={{
        position: 'fixed', bottom: 12, right: 14, width: 38, height: 38,
        objectFit: 'contain', opacity: 0.12, pointerEvents: 'none', zIndex: 5, userSelect: 'none'
      }} />
    </div>
  );
}
